import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const normalizeBase = (u) => (u || "").replace(/\/+$/, "");

const SkillsDefinitionTool = ({ backEndUrl, platformUrl, tenantId }) => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = query.get("token") || "";
    const userUuid = query.get("uuid") || "";
    const courseUuid = query.get("courseUuid") || "";

    const storedCourseId = localStorage.getItem("courseId") || "";
    const [courseId, setCourseId] = useState(storedCourseId);
    const [courseCode, setCourseCode] = useState("");

    const [role, setRole] = useState("Student");
    const [courseName, setCourseName] = useState("");
    const [result, setResult] = useState("");
    const [citations, setCitations] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [resultTitle, setResultTitle] = useState("Result");
    const [resultMode, setResultMode] = useState("text");

    const [uploadedSkills, setUploadedSkills] = useState([]);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [currentDescription, setCurrentDescription] = useState("");
    const [updatedDescriptionPreview, setUpdatedDescriptionPreview] = useState("");
    const [updateStatus, setUpdateStatus] = useState("");
    const [badgeName, setBadgeName] = useState("Culinary Foundations Badge");
    const fileInputRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        async function resolveCourseUuidToPk() {
            if (!courseUuid || !platformUrl || !token) return;

            try {
                const base = normalizeBase(platformUrl);
                const url = `${base}/learn/api/public/v1/courses/uuid:${courseUuid}`;
                const { data } = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const resolvedPk = data?.id || "";
                if (!resolvedPk || cancelled) return;

                setCourseId(resolvedPk);

                try {
                    localStorage.setItem("courseId", resolvedPk);
                } catch {}

                if (data?.name) setCourseName(data.name);
                if (data?.courseId) setCourseCode(String(data.courseId).trim());
                if (data?.description) setCurrentDescription(data.description);
            } catch (e) {
                console.error("Failed to resolve courseUuid -> courseId", e);
                const msg = e?.response?.data?.message || e?.message || "Failed to resolve courseUuid.";
                if (!cancelled) setError(msg);
            }
        }

        resolveCourseUuidToPk();

        return () => {
            cancelled = true;
        };
    }, [courseUuid, platformUrl, token]);

    useEffect(() => {
        let cancelled = false;

        async function fetchCourseInfo() {
            if (!courseId || !platformUrl || !token) return;

            try {
                const base = normalizeBase(platformUrl);

                const courseUrl = `${base}/learn/api/public/v1/courses/${courseId}`;
                const { data } = await axios.get(courseUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!cancelled && data?.name) {
                    setCourseName(data.name);
                }

                if (!cancelled) {
                    setCurrentDescription(data?.description || "");
                    setCourseCode(String(data?.courseId || "").trim());
                }

                if (userUuid) {
                    try {
                        const memUrl = `${base}/learn/api/public/v1/courses/${courseId}/users/uuid:${userUuid}`;
                        const { data: mem } = await axios.get(memUrl, {
                            headers: { Authorization: `Bearer ${token}` },
                        });

                        if (!cancelled && mem?.courseRoleId) {
                            setRole(mem.courseRoleId);
                        }
                    } catch {}
                }
            } catch (e) {
                console.error("Failed to fetch course info", e);
            }
        }

        fetchCourseInfo();

        return () => {
            cancelled = true;
        };
    }, [courseId, platformUrl, token, userUuid]);

    const guards = useMemo(() => {
        const issues = [];
        if (!backEndUrl) issues.push("Missing backEndUrl prop.");
        if (!platformUrl) issues.push("Missing platformUrl prop.");
        if (!token) issues.push("Missing token in URL.");
        if (!courseId) {
            if (courseUuid) {
                issues.push("Missing courseId: courseUuid was provided but could not be resolved.");
            } else {
                issues.push("Missing courseId in localStorage.");
            }
        }
        return issues;
    }, [backEndUrl, platformUrl, token, courseId, courseUuid]);

    async function runPrompt(prompt, title, mode = "text") {
        setError("");
        setResult("");
        setCitations([]);
        setResultTitle(title);
        setResultMode(mode);

        if (busy) return;
        if (guards.length) {
            setError(guards.join(" "));
            return;
        }

        setBusy(true);

        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

            const payload = {
                platformUrl,
                prompt,
                role,
                courseId,
                timeZone,
            };

            const { data } = await axios.post(`${normalizeBase(backEndUrl)}/chat`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                },
            });

            setResult(data?.answer || "No result was returned.");
            setCitations(Array.isArray(data?.citations) ? data.citations : []);
        } catch (e) {
            console.error("Error calling prompt API", e);
            const msg = e?.response?.data?.error || e?.message || "Error calling prompt API.";
            setError(msg);
        } finally {
            setBusy(false);
        }
    }

    function determineSkills() {
        runPrompt(
            "Can you tell me what skills are taught in this course?",
            "Skills Taught in This Course",
            "text"
        );
    }

    function suggestOutcomes() {
        runPrompt(
            "Please suggest course outcomes based on the skills taught in this course. For each outcome, return only these fields in this exact format: Identifier: ... Title: ... Statement: ... Always generate the Identifier using the course ID prefix followed by an underscore and a two-digit sequence number, such as PREFIX_01, PREFIX_02, PREFIX_03. Use the actual course ID prefix for this course. Use general best practices, Blackboard goals and outcomes conventions, and Bloom's Taxonomy naming conventions. Make the Title brief and the Statement a fully written student outcome suitable for Blackboard outcome creation.",
            "Suggested Outcomes",
            "outcomes"
        );
    }

    function makeBadgeSkills() {
        runPrompt(
            "Can you generate the skill definitions to be listed in an open badge that represents these skills? Return only the skill definitions. Keep each skill definition to 5 or 6 words maximum. Do not use commas inside any individual skill definition.",
            "Badge Skill Definitions",
            "badgeSkills"
        );
    }

    function truncate(s = "", n = 42) {
        return s.length > n ? s.slice(0, n - 1) + "…" : s;
    }

    function renderPlain(s) {
        return String(s || "")
            .split(/\n+/)
            .map((p, i) => (
                <p key={i} style={{ margin: "0 0 8px 0" }}>
                    {p}
                </p>
            ));
    }

    function escapeHtml(value) {
        return String(value || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    function parseOutcomes(text) {
        const raw = String(text || "").replace(/\r/g, "").trim();
        if (!raw) return [];

        const normalized = raw.replace(/\n{3,}/g, "\n\n");

        const blocks = normalized
            .split(/\n\s*\n(?=(?:\d+\.\s*)?Identifier\s*:)/i)
            .map((b) => b.trim())
            .filter(Boolean);

        const rows = [];

        for (const block of blocks) {
            const identifierMatch = block.match(/(?:^|\n)(?:\d+\.\s*)?Identifier\s*:\s*(.+)/i);
            const titleMatch = block.match(/(?:^|\n)Title\s*:\s*(.+)/i);
            const statementMatch = block.match(/(?:^|\n)Statement\s*:\s*([\s\S]+?)(?=\n(?:Identifier|Title)\s*:|$)/i);

            if (identifierMatch || titleMatch || statementMatch) {
                rows.push({
                    identifier: identifierMatch?.[1]?.trim() || "",
                    title: titleMatch?.[1]?.trim() || "",
                    statement: statementMatch?.[1]?.trim() || "",
                });
            }
        }

        if (rows.length > 0) return rows;

        const lineRows = [];
        const lines = normalized
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);

        let current = null;

        const pushCurrent = () => {
            if (current && (current.identifier || current.title || current.statement)) {
                lineRows.push({
                    identifier: current.identifier || "",
                    title: current.title || "",
                    statement: current.statement || "",
                });
            }
        };

        for (const line of lines) {
            const clean = line.replace(/^[-*•]\s*/, "").trim();

            const identifierMatch = clean.match(/^(?:\d+\.\s*)?Identifier\s*:\s*(.+)$/i);
            const titleMatch = clean.match(/^Title\s*:\s*(.+)$/i);
            const statementMatch = clean.match(/^Statement\s*:\s*(.+)$/i);
            const pipeMatch = clean.match(/^(.+?)\s*\|\s*(.+?)\s*\|\s*(.+)$/);

            if (pipeMatch) {
                pushCurrent();
                current = null;
                lineRows.push({
                    identifier: pipeMatch[1].trim(),
                    title: pipeMatch[2].trim(),
                    statement: pipeMatch[3].trim(),
                });
                continue;
            }

            if (identifierMatch) {
                pushCurrent();
                current = {
                    identifier: identifierMatch[1].trim(),
                    title: "",
                    statement: "",
                };
                continue;
            }

            if (titleMatch) {
                if (!current) current = { identifier: "", title: "", statement: "" };
                current.title = titleMatch[1].trim();
                continue;
            }

            if (statementMatch) {
                if (!current) current = { identifier: "", title: "", statement: "" };
                current.statement = statementMatch[1].trim();
                continue;
            }

            if (
                current &&
                !/^Subject\s*:/i.test(clean) &&
                !/^(?:\d+\.\s*)?Identifier\s*:/i.test(clean) &&
                !/^Title\s*:/i.test(clean)
            ) {
                current.statement = current.statement
                    ? `${current.statement} ${clean}`.trim()
                    : clean;
            }
        }

        pushCurrent();
        return lineRows;
    }

    function parseBadgeSkills(text) {
        return String(text || "")
            .split(/\n|;/)
            .map((line) => line.trim())
            .map((line) => line.replace(/^[-*•]\s*/, "").trim())
            .filter(Boolean)
            .map((line) => line.replace(/\.$/, "").trim())
            .filter(Boolean);
    }

    function parseDelimitedLine(line, delimiter) {
        const values = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i += 1) {
            const ch = line[i];
            const next = line[i + 1];

            if (ch === '"') {
                if (inQuotes && next === '"') {
                    current += '"';
                    i += 1;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === delimiter && !inQuotes) {
                values.push(current);
                current = "";
            } else {
                current += ch;
            }
        }

        values.push(current);
        return values.map((v) => String(v || "").trim());
    }

    function detectDelimiter(headerLine) {
        const commaCount = (headerLine.match(/,/g) || []).length;
        const tabCount = (headerLine.match(/\t/g) || []).length;
        return tabCount > commaCount ? "\t" : ",";
    }

    function parseCsvRows(text) {
        const lines = String(text || "")
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n")
            .filter((line) => line.trim() !== "");

        if (!lines.length) return [];

        const delimiter = detectDelimiter(lines[0]);
        const headers = parseDelimitedLine(lines[0], delimiter);
        const rows = [];

        for (let i = 1; i < lines.length; i += 1) {
            const values = parseDelimitedLine(lines[i], delimiter);
            const row = {};

            headers.forEach((header, idx) => {
                row[header || `Column${idx + 1}`] = values[idx] ?? "";
            });

            rows.push(row);
        }

        return rows;
    }

    function copyToClipboard(value) {
        if (!value) return;
        navigator.clipboard?.writeText(value).catch(() => {});
    }

    function extractSkillsAndBadgeFromRows(rows, liveCourseCode) {
        if (!Array.isArray(rows) || rows.length === 0) {
            return { skills: [], badgeFromFile: "", matchedRowCount: 0 };
        }

        const normalizedRows = rows.filter((row) => row && typeof row === "object");
        if (normalizedRows.length === 0) {
            return { skills: [], badgeFromFile: "", matchedRowCount: 0 };
        }

        const keys = Object.keys(normalizedRows[0]);

        const normalizeKey = (value) =>
            String(value || "")
                .trim()
                .toLowerCase()
                .replace(/\s+/g, " ");

        const skillColumns = ["skill", "skills", "name", "title", "definition"];
        const courseIdColumns = [
            "course id",
            "courseid",
            "course_id",
            "course",
            "course prefix",
            "courseprefix",
            "course_prefix",
        ];
        const badgeColumns = [
            "badge",
            "badge earned",
            "badgeearned",
            "badge name",
            "badgename",
            "badge_name",
        ];

        const skillKey =
            keys.find((key) => skillColumns.includes(normalizeKey(key))) ||
            keys.find((key) =>
                normalizedRows.some((row) => {
                    const value = row[key];
                    return value !== undefined && value !== null && String(value).trim() !== "";
                })
            );

        if (!skillKey) {
            return { skills: [], badgeFromFile: "", matchedRowCount: 0 };
        }

        const courseIdKey = keys.find((key) => courseIdColumns.includes(normalizeKey(key)));
        const badgeKey = keys.find((key) => badgeColumns.includes(normalizeKey(key)));

        let filteredRows = normalizedRows;

        if (courseIdKey && liveCourseCode) {
            const actualCourseCode = String(liveCourseCode).trim().toUpperCase();

            filteredRows = normalizedRows.filter((row) => {
                const csvCourseId = String(row[courseIdKey] ?? "").trim().toUpperCase();
                if (!csvCourseId) return false;
                return actualCourseCode.startsWith(csvCourseId);
            });
        }

        const skills = Array.from(
            new Set(
                filteredRows
                    .map((row) => row[skillKey])
                    .map((value) => String(value ?? "").trim())
                    .filter(Boolean)
            )
        );

        let badgeFromFile = "";

        if (badgeKey) {
            const badges = Array.from(
                new Set(
                    filteredRows
                        .map((row) => row[badgeKey])
                        .map((value) => String(value ?? "").trim())
                        .filter(Boolean)
                )
            );

            badgeFromFile = badges[0] || "";
        }

        return {
            skills,
            badgeFromFile,
            matchedRowCount: filteredRows.length,
        };
    }

    function stripManagedCourseSections(description) {
        return String(description || "")
            .replace(
                /<br><br>Skills Taught in this Course<br><br>[\s\S]*?(?=<br><br>Badge Earned<br><br>|$)/i,
                ""
            )
            .replace(/<br><br>Badge Earned<br><br>[\s\S]*$/i, "")
            .trim();
    }

    function buildDescriptionWithSkills(existingDescription, skills, badge = "Culinary Foundations Badge") {
        const cleanSkills = Array.from(
            new Set(
                (skills || [])
                    .map((s) => String(s || "").trim())
                    .filter(Boolean)
            )
        );

        const source = stripManagedCourseSections(existingDescription);

        const purposeMatch = source.match(/Purpose<br><br>([\s\S]*?)$/i);

        const cleanedPurpose =
            purposeMatch?.[1]?.trim() ||
            "Introduces students to the culinary profession, kitchen organization, tools, terminology, and career pathways.";

        const skillLines = cleanSkills.map((skill) => `* ${escapeHtml(skill)}`).join("<br>");

        return [
            "Purpose",
            "<br><br>",
            cleanedPurpose,
            "<br><br>",
            "Skills Taught in this Course",
            "<br><br>",
            skillLines,
            "<br><br>",
            "Badge Earned",
            "<br><br>",
            `* ${escapeHtml(badge)}`,
        ].join("");
    }

    async function handleSpreadsheetUpload(event) {
        const file = event.target.files?.[0];
        if (!file) return;

        setError("");
        setUpdateStatus("");
        setUploadedSkills([]);
        setUploadedFileName(file.name);

        try {
            const text = await file.text();
            const rows = parseCsvRows(text);
            const { skills, badgeFromFile, matchedRowCount } = extractSkillsAndBadgeFromRows(rows, courseCode);

            if (!skills.length) {
                throw new Error(
                    `No matching skills were found in the file for this course. Current course code: ${courseCode || "(unknown)"}`
                );
            }

            const effectiveBadge = badgeFromFile || badgeName;

            setUploadedSkills(skills);

            if (badgeFromFile) {
                setBadgeName(badgeFromFile);
            }

            const preview = buildDescriptionWithSkills(currentDescription, skills, effectiveBadge);
            setUpdatedDescriptionPreview(preview);

            setResultTitle("Uploaded Skills");
            setResultMode("uploadedSkills");
            setResult("");
            setCitations([]);

            setUpdateStatus(
                badgeFromFile
                    ? `Loaded ${skills.length} skills from ${file.name} using ${matchedRowCount} matching rows. Badge set to "${badgeFromFile}".`
                    : `Loaded ${skills.length} skills from ${file.name} using ${matchedRowCount} matching rows.`
            );
        } catch (e) {
            console.error("Failed to parse CSV", e);
            setError(e?.message || "Failed to read CSV.");
        } finally {
            if (event.target) {
                event.target.value = "";
            }
        }
    }

    async function refreshCurrentCourseDescription() {
        if (busy) return;
        if (guards.length) {
            setError(guards.join(" "));
            return;
        }

        setBusy(true);
        setError("");
        setUpdateStatus("");

        try {
            const base = normalizeBase(platformUrl);
            const courseUrl = `${base}/learn/api/public/v1/courses/${courseId}`;
            const { data } = await axios.get(courseUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const desc = data?.description || "";
            setCurrentDescription(desc);
            setCourseCode(String(data?.courseId || "").trim());

            if (uploadedSkills.length > 0) {
                setUpdatedDescriptionPreview(buildDescriptionWithSkills(desc, uploadedSkills, badgeName));
            }

            setUpdateStatus("Fetched the current course description.");
        } catch (e) {
            console.error("Failed to fetch course description", e);
            setError(e?.response?.data?.message || e?.message || "Failed to fetch course description.");
        } finally {
            setBusy(false);
        }
    }

    async function updateCourseDescriptionWithSkills() {
        if (busy) return;
        if (guards.length) {
            setError(guards.join(" "));
            return;
        }
        if (!uploadedSkills.length) {
            setError("Please upload a CSV file with skills first.");
            return;
        }

        setBusy(true);
        setError("");
        setUpdateStatus("");

        try {
            const base = normalizeBase(platformUrl);
            const courseUrl = `${base}/learn/api/public/v1/courses/${courseId}`;

            const currentResp = await axios.get(courseUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const existingDescription = currentResp?.data?.description || "";
            setCurrentDescription(existingDescription);
            setCourseCode(String(currentResp?.data?.courseId || "").trim());

            const newDescription = buildDescriptionWithSkills(existingDescription, uploadedSkills, badgeName);
            setUpdatedDescriptionPreview(newDescription);

            await axios.patch(
                courseUrl,
                { description: newDescription },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setUpdateStatus(`Updated the course description with ${uploadedSkills.length} skills.`);
            setResultTitle("Course Description Updated");
            setResultMode("text");
            setResult("The course description was updated successfully.");
            setCitations([]);
        } catch (e) {
            console.error("Failed to update course description", e);
            setError(
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Failed to update course description."
            );
        } finally {
            setBusy(false);
        }
    }

    const outcomeRows = useMemo(() => {
        if (resultMode !== "outcomes") return [];
        return parseOutcomes(result);
    }, [result, resultMode]);

    const badgeSkillsList = useMemo(() => {
        if (resultMode !== "badgeSkills") return [];
        return parseBadgeSkills(result);
    }, [result, resultMode]);

    const badgeSkillsCsv = useMemo(() => {
        return badgeSkillsList.join(", ");
    }, [badgeSkillsList]);

    const btnPrimary = {
        padding: "8px 12px",
        borderRadius: 6,
        border: "1px solid #2c6bed",
        background: "#2c6bed",
        color: "#fff",
        fontWeight: 600,
        cursor: "pointer",
        fontSize: 13,
    };

    const btnSecondary = {
        padding: "8px 12px",
        borderRadius: 6,
        border: "1px solid #ddd",
        background: "#fff",
        color: "#333",
        fontWeight: 600,
        cursor: "pointer",
        fontSize: 13,
    };

    const inputStyle = {
        width: "100%",
        padding: "10px 12px",
        borderRadius: 6,
        border: "1px solid #ddd",
        fontFamily: "inherit",
        fontSize: 13,
        boxSizing: "border-box",
    };

    const cardStyle = {
        padding: 12,
        border: "1px solid #ddd",
        borderRadius: 8,
        background: "#fff",
    };

    const errorStyle = {
        marginTop: 8,
        padding: "8px 10px",
        borderRadius: 6,
        background: "#fff3f3",
        border: "1px solid #ffd4d4",
        color: "#8a0000",
        fontSize: 12,
    };

    const successStyle = {
        marginTop: 8,
        padding: "8px 10px",
        borderRadius: 6,
        background: "#f2fff3",
        border: "1px solid #cfeecf",
        color: "#166534",
        fontSize: 12,
    };

    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: 8,
    };

    const thtdStyle = {
        border: "1px solid #ddd",
        padding: "8px",
        textAlign: "left",
        verticalAlign: "top",
        fontSize: 13,
    };

    const textareaStyle = {
        width: "100%",
        padding: "10px 12px",
        borderRadius: 6,
        border: "1px solid #ddd",
        fontFamily: "inherit",
        fontSize: 13,
        resize: "vertical",
        boxSizing: "border-box",
    };

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gap: 12 }}>
            <div style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <h2 style={{ margin: 0 }}>Skills Definition Tool</h2>

                    <span
                        style={{
                            background: role.toLowerCase().startsWith("instructor") ? "#eef7ff" : "#f6fff0",
                            border: "1px solid #ddd",
                            borderRadius: 999,
                            padding: "4px 8px",
                            fontSize: 12,
                        }}
                    >
                        Role: {role}
                    </span>

                    <span
                        style={{
                            background: "#fafafa",
                            border: "1px solid #eee",
                            borderRadius: 999,
                            padding: "4px 8px",
                            fontSize: 12,
                        }}
                    >
                        Course: {courseName || courseId || (courseUuid ? `uuid:${courseUuid}` : "UNKNOWN")}
                    </span>

                    {courseCode && (
                        <span
                            style={{
                                background: "#fafafa",
                                border: "1px solid #eee",
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 12,
                            }}
                        >
                            Course Code: {courseCode}
                        </span>
                    )}
                </div>

                {guards.length > 0 && (
                    <div style={errorStyle}>
                        <strong>Setup issue:</strong> {guards.join(" ")}
                    </div>
                )}

                {error && (
                    <div style={errorStyle}>
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {updateStatus && <div style={successStyle}>{updateStatus}</div>}
            </div>

            <div style={cardStyle}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        type="button"
                        onClick={determineSkills}
                        disabled={busy || !!guards.length}
                        style={btnPrimary}
                    >
                        {busy ? "Working..." : "Determine Skills"}
                    </button>

                    <button
                        type="button"
                        onClick={suggestOutcomes}
                        disabled={busy || !!guards.length}
                        style={btnPrimary}
                    >
                        {busy ? "Working..." : "Suggest Outcomes"}
                    </button>

                    <button
                        type="button"
                        onClick={makeBadgeSkills}
                        disabled={busy || !!guards.length}
                        style={btnPrimary}
                    >
                        {busy ? "Working..." : "Make Badge Skills"}
                    </button>
                </div>
            </div>

            <div style={cardStyle}>
                <h3 style={{ marginTop: 0 }}>Upload Skills CSV</h3>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleSpreadsheetUpload}
                    style={{ display: "none" }}
                />

                <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Badge Earned</div>
                    <input
                        type="text"
                        value={badgeName}
                        onChange={(e) => {
                            const nextBadge = e.target.value;
                            setBadgeName(nextBadge);
                            if (uploadedSkills.length > 0) {
                                setUpdatedDescriptionPreview(
                                    buildDescriptionWithSkills(currentDescription, uploadedSkills, nextBadge)
                                );
                            }
                        }}
                        style={inputStyle}
                    />

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={busy || !!guards.length}
                            style={btnSecondary}
                        >
                            Choose CSV
                        </button>

                        <button
                            type="button"
                            onClick={refreshCurrentCourseDescription}
                            disabled={busy || !!guards.length}
                            style={btnSecondary}
                        >
                            Refresh Current Description
                        </button>

                        <button
                            type="button"
                            onClick={updateCourseDescriptionWithSkills}
                            disabled={busy || !!guards.length || uploadedSkills.length === 0}
                            style={btnPrimary}
                        >
                            {busy ? "Updating..." : "Update Course Description"}
                        </button>
                    </div>
                </div>

                {uploadedFileName && (
                    <div style={{ marginTop: 10, fontSize: 13 }}>
                        <strong>File:</strong> {uploadedFileName}
                    </div>
                )}

                {uploadedSkills.length > 0 && (
                    <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                        <div style={{ fontSize: 13 }}>
                            <strong>Loaded skills:</strong> {uploadedSkills.length}
                        </div>

                        <textarea
                            readOnly
                            value={uploadedSkills.join("\n")}
                            rows={8}
                            style={textareaStyle}
                        />
                    </div>
                )}
            </div>

            <div style={cardStyle}>
                <h3 style={{ marginTop: 0 }}>Course Description Preview</h3>

                <div style={{ display: "grid", gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Current Description</div>
                        <textarea
                            readOnly
                            value={currentDescription}
                            rows={8}
                            style={textareaStyle}
                        />
                    </div>

                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Updated Description</div>
                        <textarea
                            readOnly
                            value={updatedDescriptionPreview}
                            rows={12}
                            style={textareaStyle}
                        />
                    </div>
                </div>
            </div>

            <div style={cardStyle}>
                <h3 style={{ marginTop: 0 }}>{resultTitle}</h3>

                {busy && <p style={{ margin: 0 }}>Thinking...</p>}

                {!busy && !result && (
                    <p style={{ margin: 0 }}>
                        Click "Determine Skills", "Suggest Outcomes", or "Make Badge Skills" to generate results.
                    </p>
                )}

                {!busy && result && resultMode === "outcomes" && outcomeRows.length > 0 && (
                    <table style={tableStyle}>
                        <thead>
                        <tr>
                            <th style={thtdStyle}>Identifier</th>
                            <th style={thtdStyle}>Title</th>
                            <th style={thtdStyle}>Statement</th>
                        </tr>
                        </thead>
                        <tbody>
                        {outcomeRows.map((row, idx) => (
                            <tr key={idx}>
                                <td style={thtdStyle}>{row.identifier}</td>
                                <td style={thtdStyle}>{row.title}</td>
                                <td style={thtdStyle}>{row.statement}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}

                {!busy && result && resultMode === "badgeSkills" && badgeSkillsList.length > 0 && (
                    <div style={{ display: "grid", gap: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Copyable comma-separated list</div>

                        <textarea
                            readOnly
                            value={badgeSkillsCsv}
                            rows={4}
                            style={textareaStyle}
                        />

                        <div>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(badgeSkillsCsv)}
                                style={btnPrimary}
                            >
                                Copy List
                            </button>
                        </div>
                    </div>
                )}

                {!busy && result && resultMode === "uploadedSkills" && uploadedSkills.length > 0 && (
                    <div style={{ display: "grid", gap: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Uploaded skills</div>
                        <textarea
                            readOnly
                            value={uploadedSkills.join("\n")}
                            rows={8}
                            style={textareaStyle}
                        />
                    </div>
                )}

                {!busy && result && (
                    resultMode === "outcomes" ? (
                        outcomeRows.length === 0 ? <div>{renderPlain(result)}</div> : null
                    ) : resultMode === "badgeSkills" ? (
                        badgeSkillsList.length === 0 ? <div>{renderPlain(result)}</div> : null
                    ) : resultMode === "uploadedSkills" ? null : (
                        <div>{renderPlain(result)}</div>
                    )
                )}

                {citations.length > 0 && (
                    <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {citations.map((c, i) => (
                            <a
                                key={i}
                                href={c.href}
                                target="_blank"
                                rel="noreferrer"
                                title={c.title}
                                style={{
                                    fontSize: 12,
                                    textDecoration: "none",
                                    borderBottom: "1px dotted #999",
                                }}
                            >
                                [{truncate(c.title, 36)}]
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SkillsDefinitionTool;